import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';

export const slashCommandPluginKey = new PluginKey('quillSlashCommand');

/**
 * Tiny external store the React slash menu subscribes to (useSyncExternalStore).
 * @returns {{getSnapshot: Function, subscribe: Function, set: Function}} Store.
 */
export function createSlashStore() {
  let snapshot = { open: false, items: [], query: '', selectedIndex: 0, clientRect: null, command: null };
  const listeners = new Set();
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set: (patch) => {
      snapshot = { ...snapshot, ...patch };
      listeners.forEach((listener) => listener());
    },
  };
}

/**
 * Normalize text for fuzzy-ish matching of slash items.
 * @param {string} value - Text.
 * @returns {string} Lowercase text.
 */
const norm = (value) => String(value || '').toLowerCase();

/**
 * "/" command menu built on @tiptap/suggestion. Items come from
 * `options.getItems(editor)` so the host can vary them by mode (document/template).
 * Each item: `{ id, title, description?, keywords?: string[], group?, run: ({editor, range}) => void }`.
 */
const SlashCommand = Extension.create({
  name: 'slashCommand',

  addOptions() {
    return {
      getItems: () => [],
    };
  },

  addStorage() {
    return { store: createSlashStore() };
  },

  addProseMirrorPlugins() {
    const { store } = this.storage;
    const { getItems } = this.options;

    /**
     * Run the item at `index` and close the menu.
     * @param {number} index - Item index.
     * @returns {boolean} Whether an item ran.
     */
    const runIndex = (index) => {
      const snap = store.getSnapshot();
      const item = snap.items[index];
      if (!item || !snap.command) return false;
      snap.command(item);
      return true;
    };

    return [
      Suggestion({
        editor: this.editor,
        pluginKey: slashCommandPluginKey,
        char: '/',
        allowSpaces: false,
        startOfLine: false,
        allow: ({ editor, state, range }) => {
          if (!editor.isEditable) return false;
          const $from = state.doc.resolve(range.from);
          return !$from.parent.type.spec.code;
        },
        items: ({ query, editor }) => {
          const q = norm(query);
          const items = getItems(editor) || [];
          if (!q) return items;
          return items.filter((item) => norm(item.title).includes(q)
            || (item.keywords || []).some((keyword) => norm(keyword).startsWith(q)));
        },
        command: ({ editor, range, props }) => {
          props.run({ editor, range });
        },
        render: () => ({
          onStart: (props) => {
            store.set({
              open: true,
              items: props.items,
              query: props.query,
              selectedIndex: 0,
              clientRect: props.clientRect || null,
              command: props.command,
            });
          },
          onUpdate: (props) => {
            const prev = store.getSnapshot();
            store.set({
              items: props.items,
              query: props.query,
              selectedIndex: props.query === prev.query ? prev.selectedIndex : 0,
              clientRect: props.clientRect || null,
              command: props.command,
            });
          },
          onKeyDown: ({ event }) => {
            const snap = store.getSnapshot();
            if (!snap.open) return false;
            const count = snap.items.length;
            if (event.key === 'Escape') {
              store.set({ open: false });
              return true;
            }
            if (!count) return false;
            if (event.key === 'ArrowDown') {
              store.set({ selectedIndex: (snap.selectedIndex + 1) % count });
              return true;
            }
            if (event.key === 'ArrowUp') {
              store.set({ selectedIndex: (snap.selectedIndex - 1 + count) % count });
              return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
              return runIndex(snap.selectedIndex);
            }
            return false;
          },
          onExit: () => {
            store.set({ open: false, items: [], query: '', clientRect: null, command: null });
          },
        }),
      }),
    ];
  },
});

export default SlashCommand;
